import React from 'react';

import {
  Chip,
  Item,
  Wrapper,
  ItemList,
  ChipsInput,
  RemoveIcon,
  InputContainer,
} from './styles';

export interface IChipItem {
  id: number | string;
  title: string;
  disabled?: boolean;
  [key: string]: any;
}

interface IChipsProps {
  chips?: IChipItem[];
  disabled?: boolean;
  onAddChip: (value: string | IChipItem) => void;
  removable?: boolean;
  defaultChips: IChipItem[];
  placeholder?: string;
  placeholderColor?: string;
  onRemoveChip: (value: IChipItem) => void;
  singleSelect?: boolean;
}

interface IChipsState {
  focused: boolean;
  inputValue: string;
  selectedChip: IChipItem['id'];
}

class SelectChips extends React.PureComponent<IChipsProps, IChipsState> {
  static defaultProps = {
    removable: true,
    placeholder: '',
    defaultChips: [],
  };

  state = {
    focused: false,
    inputValue: '',
    selectedChip: -1,
  };

  inputRef: React.RefObject<HTMLInputElement> = React.createRef();
  wrapperRef: React.RefObject<HTMLInputElement> = React.createRef();

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside, false);
  }

  handleClickOutside = (event: any) => {
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({ focused: false });
    }
  };

  handleFocus = () => {
    this.setState({ focused: true });
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  handleActionsBasedOnKey = (e: React.KeyboardEvent) => {
    if (e.which === 13 && this.state.inputValue.length > 0) {
      // user enters ENTER KEY and input is not empty
      e.preventDefault();
      if (this.props.chips) {
        // if component has autocomplete item list, so use just can add item that exists in the list
        const filteredList = this.props.chips.filter((item) => {
          return item.title.includes(this.state.inputValue);
        });
        const [first] = filteredList;
        if (first) {
          this.props.onAddChip(first);
          this.setState({ inputValue: '' });
        }
      } else {
        // if component has not autocomplete item list, so user can add any item
        this.props.onAddChip(this.state.inputValue);
        this.setState({ inputValue: '' });
      }
    } else if (e.which === 8 && this.state.inputValue.length === 0) {
      // user enters BACKSPACE KEY and input is empty
      if (this.state.selectedChip === -1) {
        // if user enters BACKSPACE KEY to select last chip
        const lastChip = this.props.defaultChips.slice(-1).pop();
        if (lastChip?.disabled !== true)
          this.setState({ selectedChip: lastChip ? lastChip.id : -1 });
      } else {
        // if user enters BACKSPACE KEY to remove selected chip
        const targetedChip = this.props.defaultChips.find(
          (item) => item.id === this.state.selectedChip
        );
        this.props.onRemoveChip(targetedChip as IChipItem);
        this.setState({ selectedChip: -1 });
      }
    } else {
      this.setState({ selectedChip: -1 });
    }
  };

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ inputValue: value });
  };

  handleRemoveChip = (chip: IChipItem) => () => {
    this.props.onRemoveChip(chip);
  };

  handleClickOnContainer = () => {
    if (this.inputRef.current) {
      this.inputRef.current.focus();
    }
  };

  handleClickOnItem = (item: IChipItem) => () => {
    this.props.onAddChip(item);
    this.setState({ inputValue: '' });
    if (this.props.singleSelect) {
      this.setState({ focused: false });
      this.inputRef.current?.blur();
    }
  };

  render() {
    const {
      chips,
      disabled,
      removable,
      placeholder,
      placeholderColor,
      defaultChips,
      singleSelect,
    } = this.props;
    return (
      <Wrapper
        ref={this.wrapperRef}
        onFocus={this.handleFocus}
        loading={disabled}
      >
        <InputContainer
          focused={!disabled && this.state.focused}
          onClick={this.handleClickOnContainer}
        >
          {defaultChips.map((chip) => (
            <Chip
              key={chip.id}
              selected={this.state.selectedChip === chip.id}
              disabled={!!chip.disabled}
            >
              {chip.title}
              {removable && !chip.disabled && (
                <RemoveIcon
                  role="button"
                  onClick={this.handleRemoveChip(chip)}
                  type={'x'}
                />
              )}
            </Chip>
          ))}
          {(!singleSelect || !defaultChips.length) && (
            <ChipsInput
              type="text"
              disabled={disabled}
              value={this.state.inputValue}
              onChange={this.handleChangeInput}
              ref={this.inputRef}
              className="browser-default"
              onKeyDown={this.handleActionsBasedOnKey}
              placeholder={placeholder}
              placeholderColor={placeholderColor}
            />
          )}
        </InputContainer>
        {chips && !disabled && this.state.focused && (
          <ItemList>
            {chips
              .filter((item) => {
                return item.title.includes(this.state.inputValue);
              })
              .filter((item) => {
                return !defaultChips.find((chip) => chip.id === item.id);
              })
              .map((item) => (
                <Item key={item.id} onClick={this.handleClickOnItem(item)}>
                  {item.title}
                </Item>
              ))}
          </ItemList>
        )}
      </Wrapper>
    );
  }
}

export default SelectChips;
